import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Spinner } from '@crazyegginc/hatch';

import { usePermissions, useSite, useSelectedSite } from '/src/hooks';

import { funnelListQuery } from '../../queries';
import { SitePickerBar } from '/src/components/site-picker/index';
import { SearchInput } from '/src/components/search-input';
import { AddSiteModal } from '/src/components/modals/AddSiteModal';

import { NoMatchesWall } from '/src/features/_global/paywalls/NoMatchesWall';
import { InstallationBanner } from '/src/components/banners/InstallationBanner';
import { NotInstalledWall } from '/src/features/web-analytics/walls/NotInstalled';

import { FunnelsFilterProvider, useFilter } from '../../funnel-filter-context';
import { FunnelTable } from './FunnelsTable';
import { NoFunnelsWall } from '../paywalls/NoFunnelsWall';
import { getFeatureName } from '/src/features/_global/utils';
import { FEATURES, ALL } from '/src/features/_global/constants';
import { SourceSelector } from '/src/components/source-selector/source-selector';
import { PaymentAlerts } from '/src/features/_global/components/PaymentAlerts';

export function FunnelsDashboardContent() {
  const { sites, selectedSite, loadingSites, reloadSites } = useSite({ enableAllSites: true });
  const permissions = usePermissions();
  const canEditSites = permissions.can('create', FEATURES.SITES).allowed;
  const navigate = useNavigate();

  if (loadingSites) {
    return (
      <div className="mt-16 flex w-full items-center justify-center">
        <Spinner />
        <div className="ml-2.5">Loading...</div>
      </div>
    );
  }

  if (!loadingSites && !sites.length) {
    return (
      <AddSiteModal
        customTitle="Add a site before creating Funnels"
        onExtraSteps={() => {
          reloadSites();
        }}
        onCancel={() => navigate('/home', { replace: true })}
        dismissable={false}
      />
    );
  }

  return (
    <>
      <SitePickerBar
        Picker={SourceSelector}
        sites={sites}
        loading={loadingSites}
        addSiteOption={canEditSites}
        pageDisplayName={`${getFeatureName(FEATURES.FUNNELS)} dashboard`}
        enableAllSites={true}
      />
      {!loadingSites && (selectedSite?.installed || selectedSite === ALL) ? (
        <FunnelsFilterProvider>
          <InstallationBanner />
          <PaymentAlerts />
          <div className="flex flex-col p-10">
            <Funnel />
          </div>
        </FunnelsFilterProvider>
      ) : (
        <>
          <InstallationBanner />
          <NotInstalledWall />
        </>
      )}
    </>
  );
}

function Funnel() {
  const { selectedSite } = useSelectedSite();

  const {
    search,
    setSearch,
    reset,
    order: { field, sort },
  } = useFilter();

  const queryResult = useInfiniteQuery({
    ...funnelListQuery({
      search,
      field,
      sort,
      siteId: selectedSite?.id ?? undefined,
    }),
    enabled: Boolean(selectedSite),
  });

  const { data, isFetching } = queryResult;
  const funnels = useMemo(
    () => data?.pages.reduce((acc, page) => [...acc, ...(page.funnelList?.funnels ?? [])], []) ?? [],
    [data],
  );

  if (data && !isFetching && funnels.length === 0 && !search) {
    return <NoFunnelsWall />;
  }

  return (
    <>
      <div className="flex w-full items-center justify-end space-x-3.75 pb-3.75">
        <SearchInput
          initialValue={search}
          search={setSearch}
          placeholder="Search Funnel Reports"
          className=" !border-solitude-500 !bg-solitude-500 !font-semibold !placeholder-dodger-blue-500/50"
        />
      </div>
      {!isFetching && selectedSite && !funnels.length && !!search ? (
        <NoMatchesWall reset={reset} text="matches, try clearing the filters" actionText="filters" />
      ) : (
        <FunnelTable funnels={funnels} queryResult={queryResult} />
      )}
    </>
  );
}
